<script lang="ts" setup>
import { computed, watch } from 'vue';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import dayjs from 'dayjs';
import AdminBaseTable, { Field } from '@/components/admin/admin-base-table.vue';
import AdminContent from '@/components/layouts/admin-content.vue';
import Header from '@/components/layouts/header.vue';
import { useRedirectWithoutManagementContracts } from '@/utils/composables/useRedirectWithoutManagementContracts';
import { ZERO_CREDIT_BALANCE_WARNING_MESSAGE } from '@/utils/credit/constants';
import { formatDate } from '@/utils/formatters';
import { useManagementContracts } from '@/utils/swr';

type AdminTableItem = {
  id: number;
  contractName: string;
  maxAccount: number;
  creditBalance: number | null;
  currentAccountCount: number;
  managingUsersCount: number;
  formattedContractPeriod: string;
  contractPeriod: Date;
  showCreditWarningIcon: boolean;
};

const redirectWithoutManagementContracts =
  useRedirectWithoutManagementContracts();
const { data: managementContracts, state } = useManagementContracts();
const contracts = computed(
  () => managementContracts.value?.management_contracts ?? [],
);

watch(
  contracts,
  changedContracts => {
    if (state.value === 'SUCCESS' && changedContracts.length === 0) {
      redirectWithoutManagementContracts();
    }
  },
  { immediate: true },
);

const fields: Field<AdminTableItem>[] = [
  {
    name: 'contractName',
    displayName: '契約名',
    isSortable: true,
    type: 'string',
    minWidth: 300,
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'currentAccountCount',
    displayName: 'アカウント',
    isSortable: true,
    type: 'number',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'creditBalance',
    displayName: 'クレジット残高',
    isSortable: true,
    type: 'number',
    fieldPosition: 'left',
    cellPosition: 'left',
  } as const,
  {
    name: 'managingUsersCount',
    displayName: '契約管理者',
    isSortable: true,
    type: 'number',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'contractPeriod',
    displayName: '契約期間',
    isSortable: true,
    type: 'date',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
];

const items = computed<AdminTableItem[]>(() => {
  return contracts.value.map(contract => {
    const isExpired = dayjs(contract.end_date).isBefore(dayjs(), 'day');
    // 警告アイコンの表示条件: 契約が期限切れでない かつ クレジット残高が0
    const showCreditWarningIcon = !isExpired && contract.credit_balance === 0;

    return {
      contractName: contract.contract_name,
      maxAccount: contract.max_account,
      creditBalance: contract.credit_balance,
      currentAccountCount: contract.current_account_count,
      managingUsersCount: contract.managing_users_count,
      formattedContractPeriod: `${formatDate(contract.start_date)}〜${formatDate(contract.end_date)}`,
      contractPeriod: new Date(contract.start_date),
      id: contract.id,
      showCreditWarningIcon,
    };
  });
});
</script>

<template>
  <div class="contract-admin">
    <Header title="契約・クレジット管理" header-width="100%" />
    <AdminContent>
      <div>
        <div class="title">
          <span class="c-title-bold--m">契約</span
          ><span class="count c-text-regular--s">・{{ items.length }}件</span>
        </div>
        <div class="spacing-16"></div>
        <div
          v-if="state === 'SUCCESS' && contracts.length === 0"
          class="empty-message c-text c-text--s"
        >
          表示できる契約がありません。
        </div>
        <AdminBaseTable v-else :items="items" :fields="fields">
          <template #cell-contractName="{ item }">
            <VTooltip placement="bottom">
              <div class="contract-name-cell">
                {{ item.contractName }}
              </div>
              <template #popper>
                <span>{{ item.contractName }}</span>
              </template>
            </VTooltip>
          </template>
          <template #cell-currentAccountCount="{ item }">
            {{ item.currentAccountCount }} / {{ item.maxAccount }}
          </template>
          <template #cell-creditBalance="{ item }">
            <div class="credit-balance-cell">
              <router-link
                class="page-link"
                :to="`contracts/${item.id}/credits/usages`"
              >
                {{ item.creditBalance?.toLocaleString() ?? 0 }}</router-link
              >
              <DgrIcon
                v-if="item.showCreditWarningIcon"
                v-tooltip.bottom="{
                  content: ZERO_CREDIT_BALANCE_WARNING_MESSAGE,
                }"
                size="small"
                name="exclamation-circle"
              />
            </div>
          </template>
          <template #cell-managingUsersCount="{ item }">
            <router-link
              class="page-link"
              :to="`contracts/${item.id}/members`"
              >{{ `${item.managingUsersCount}人` }}</router-link
            >
          </template>
          <template #cell-contractPeriod="{ item }">
            {{ item.formattedContractPeriod }}
          </template>
        </AdminBaseTable>
      </div>
    </AdminContent>
  </div>
</template>

<style lang="scss" scoped>
.contract-admin {
  width: 100%;
  .title {
    display: flex;
    align-items: center;
    .count {
      color: $color-gray600;
    }
  }
  .contract-name-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .credit-balance-cell {
    display: flex;
    gap: 4px;
    align-items: center;
  }
  .page-link {
    text-decoration: underline;
  }
  .empty-message {
    padding: 24px;
    border: 1px solid $color-gray400;
    border-radius: 4px;
    background: $color-white;
    color: $color-gray1000;
  }
}
</style>
